'use client'

import { useState, useEffect } from 'react'
import { useAccount, useConnect, useDisconnect } from 'wagmi'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

export function WalletReminderBot() {
  const { address } = useAccount()
  const { connectors, connect } = useConnect()
  const { disconnect } = useDisconnect()
  const [isVisible, setIsVisible] = useState(false)
  const [message, setMessage] = useState('')
  const [showWalletOptions, setShowWalletOptions] = useState(false)
  const [showWalletInfo, setShowWalletInfo] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Show reminder when user is not connected
  const showReminder = (customMessage?: string) => {
    if (address) return // Don't show if wallet is connected
    
    setMessage(customMessage || "Hey! 👋 You need to connect your wallet first!")
    setShowWalletOptions(true)
    setShowWalletInfo(false)
    setIsVisible(true)
    setIsAnimating(true)
    
    // Stop bouncing after 2 seconds but keep bubble open
    setTimeout(() => {
      setIsAnimating(false)
    }, 2000)
    
    // Auto-hide after 10 seconds when showing wallet options
    setTimeout(() => {
      setIsVisible(false)
      setShowWalletOptions(false)
    }, 10000)
  }

  // Hide the bot
  const hideBot = () => {
    setIsVisible(false)
    setShowWalletOptions(false)
    setShowWalletInfo(false)
  }

  // Handle wallet connection
  const handleConnect = (connector: any) => {
    connect({ connector })
    hideBot()
  }

  // Handle bot click to show wallet options or info
  const handleBotClick = () => {
    if (address) {
      // Show wallet info and disconnect option
      setMessage("Wallet Connected! 🔗")
      setShowWalletInfo(true)
      setShowWalletOptions(false)
      setIsVisible(true)
      setIsAnimating(true)
      
      // Stop bouncing after 1 second
      setTimeout(() => {
        setIsAnimating(false)
      }, 1000)
      
      // Auto-hide after 8 seconds
      setTimeout(() => {
        setIsVisible(false)
        setShowWalletInfo(false)
      }, 8000)
    } else {
      showReminder("Choose your wallet to connect! 🚀")
    }
  }

  // Handle disconnect
  const handleDisconnect = () => {
    disconnect()
    hideBot()
  }

  // Expose the showReminder function globally so other components can trigger it
  useEffect(() => {
    // @ts-ignore
    window.showWalletReminder = showReminder
    
    return () => {
      // @ts-ignore
      delete window.showWalletReminder
    }
  }, [address])

  // Always render the bot - it serves as wallet status indicator

  return (
    <>
      {/* Main bot - always visible when wallet not connected */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="relative">
          {/* Bot character */}
          <div 
            className={`
              ${address 
                ? 'bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600/50' 
                : 'bg-gradient-to-br from-green-800 to-green-900 border-2 border-green-600/50'
              }
              rounded-full w-16 h-16 
              flex items-center justify-center 
              shadow-lg ${address ? 'shadow-blue-900/50' : 'shadow-green-900/50'}
              cursor-pointer
              hover:scale-105 
              transition-all duration-300
              ${isAnimating ? 'animate-bounce' : ''}
            `}
            onClick={handleBotClick}
          >
            <div className="text-2xl">
              {address ? '🔗' : '🤖'}
            </div>
          </div>
          
          {/* Pulse rings - only show when animating */}
          {isAnimating && (
            <>
              <div className={`absolute inset-0 rounded-full border-2 animate-ping ${
                address ? 'border-blue-500/30' : 'border-green-500/30'
              }`}></div>
              <div className={`absolute inset-2 rounded-full border animate-ping animation-delay-75 ${
                address ? 'border-blue-400/20' : 'border-green-400/20'
              }`}></div>
            </>
          )}
        </div>
      </div>

      {/* Message bubble */}
      {isVisible && (
        <div className="fixed bottom-24 right-6 z-50 animate-in slide-in-from-bottom-2 duration-300">
          <div className="relative max-w-sm">
            {/* Speech bubble */}
            <div className="bg-black border-2 border-green-600/50 rounded-lg p-4 shadow-lg shadow-green-900/30">
              <div className="flex items-start justify-between gap-2 mb-3">
                <p className="text-green-400 text-sm font-mono leading-relaxed">
                  {message}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={hideBot}
                  className="h-6 w-6 p-0 text-green-600/60 hover:text-green-400 hover:bg-green-900/20 flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              
              {/* Wallet connection options */}
              {showWalletOptions && (
                <div className="space-y-2">
                  <div className="border-t border-green-700/30 pt-3">
                    <p className="text-green-600/80 text-xs font-mono mb-2">Choose your wallet:</p>
                    <div className="space-y-2">
                      {connectors.map((connector) => (
                        <Button
                          key={connector.uid}
                          onClick={() => handleConnect(connector)}
                          variant="outline"
                          size="sm"
                          className="w-full bg-green-900/20 border-green-700/50 text-green-400 hover:bg-green-800/30 hover:text-green-300 font-mono text-xs justify-start"
                        >
                          <span className="mr-2">
                            {connector.name === 'MetaMask' ? '🦊' : 
                             connector.name === 'WalletConnect' ? '📱' : 
                             connector.name === 'Coinbase Wallet' ? '🔵' : '💼'}
                          </span>
                          {connector.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Wallet info and disconnect */}
              {showWalletInfo && address && (
                <div className="space-y-3">
                  <div className="border-t border-blue-700/30 pt-3">
                    <p className="text-blue-600/80 text-xs font-mono mb-2">Connected Wallet:</p>
                    <div className="bg-blue-900/10 border border-blue-700/20 rounded p-2 mb-3">
                      <p className="text-blue-400 text-xs font-mono">
                        {address.slice(0, 6)}...{address.slice(-4)}
                      </p>
                      <p className="text-blue-500/60 text-xs font-mono mt-1">
                        Base Sepolia
                      </p>
                    </div>
                    <Button
                      onClick={handleDisconnect}
                      variant="outline"
                      size="sm"
                      className="w-full bg-red-900/20 border-red-700/50 text-red-400 hover:bg-red-800/30 hover:text-red-300 font-mono text-xs"
                    >
                      🔌 Disconnect Wallet
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Speech bubble tail */}
            <div className="absolute bottom-0 right-8 transform translate-y-full">
              <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-green-600/50"></div>
              <div className="w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-black absolute bottom-0 left-1"></div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// Helper function that other components can use
export const triggerWalletReminder = (message?: string) => {
  // @ts-ignore
  if (typeof window !== 'undefined' && window.showWalletReminder) {
    // @ts-ignore
    window.showWalletReminder(message)
  }
} 
 